import { Box, type Theme } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";

import { InfoBanner } from "../../../components/InfoBanner/InfoBanner";
import { type InviteErrorReason } from "./types";

const ERROR_MESSAGES: Record<InviteErrorReason, string> = {
  SHIFT_OVERWORKING:
    "This invite cannot be sent because the worker would exceed the mandatory 17-hour limit.",
  SHIFT_IN_PAST: "This shift has already started.",
  WORKER_MISSING_LICENSE: "This professional is missing a required license.",
  WORKER_MISSING_DOCUMENTS: "This professional is missing required documents.",
  WORKER_MISSING_REQUIREMENTS: "This professional is missing required documents for this shift.",
  WORKER_DISABLED_TEST_ACCOUNT: "This is a disabled test account.",
  WORKER_CANT_CLAIM_SHIFT: "This professional cannot claim this shift.",
  WORKER_PAYMENTS_DISABLED: "This professional has payments disabled.",
  WORKER_PENDING_DOCUMENTS: "This professional has documents pending review.",
  WORKER_UNSIGNED_AGREEMENT: "This professional hasn't signed the required agreement.",
  WORKER_DEACTIVATED: "This professional's account has been deactivated.",
  WORKER_BLOCKED: "This professional has been blocked by your workplace.",
  FACILITY_SUSPENDED: "This facility has been suspended.",
  WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK:
    "This professional is missing data for background check.",
  WORKER_HAS_REJECTED_BACKGROUND_CHECK: "This professional has a rejected background check.",
  QUALIFICATION_NOT_ENABLED:
    "This qualification is hidden from your workplace's calendar. Re-enable it in Settings or choose another qualification.",
};

export function getErrorMessage(errorReasons: InviteErrorReason[], detail?: string): string {
  if (errorReasons.length !== 1) {
    return "This professional cannot receive this invite due to multiple issues.";
  }

  if (errorReasons[0] === "WORKER_CANT_CLAIM_SHIFT" && detail) {
    return detail;
  }

  return ERROR_MESSAGES[errorReasons[0]];
}

interface InviteErrorModalProps {
  modalState: UseModalState;
  errorReasons: InviteErrorReason[];
  missingDocuments?: string[];
  /** Backend detail message to display instead of the generic fallback. */
  detail?: string;
  professionalName?: string;
  qualificationName?: string;
  /**
   * When provided, the primary action lets the user pick a different professional.
   * When absent (preselected-worker flow), only a "Close" action is shown.
   */
  onSelectAnother?: () => void;
  onDismiss: () => void;
}

/**
 * Modal displayed when invite validation fails with blocking errors.
 * Shows the error reasons and prompts user to select another professional.
 */
export function InviteErrorModal(props: InviteErrorModalProps) {
  const {
    modalState,
    errorReasons,
    missingDocuments,
    detail,
    professionalName,
    qualificationName,
    onSelectAnother,
    onDismiss,
  } = props;
  const isBlockedError = errorReasons.includes("WORKER_BLOCKED");
  // Workplace/qualification issues aren't about the selected professional —
  // selecting another won't resolve them. Suppress the professional-centric
  // chrome (banner title, primary action, helper text) when that's the only
  // reason.
  const isWorkplaceQualificationIssue =
    errorReasons.length === 1 && errorReasons[0] === "QUALIFICATION_NOT_ENABLED";

  const handleSelectAnother = () => {
    modalState.closeModal();
    onSelectAnother?.();
  };

  const handleDismiss = () => {
    modalState.closeModal();
    onDismiss();
  };

  const hasMissingDocuments =
    errorReasons.includes("WORKER_MISSING_REQUIREMENTS") &&
    missingDocuments !== undefined &&
    missingDocuments.length > 0;

  const errorMessage = getErrorMessage(errorReasons, detail);

  const bannerTitle = isWorkplaceQualificationIssue
    ? `Cannot post invite for ${qualificationName ?? "this"} qualification`
    : `Unable to invite ${professionalName ?? "this professional"}`;

  let footerHelperText =
    "Re-enable this qualification in your workplace settings, or choose a different qualification for this shift.";
  if (!isWorkplaceQualificationIssue) {
    footerHelperText = onSelectAnother
      ? "Please select another professional to invite."
      : "This professional cannot receive this invite.";
  }

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      showCloseButton={!isBlockedError}
      title="Unable to send invite"
      footerContent={
        isWorkplaceQualificationIssue || !onSelectAnother ? (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: "Close",
              onClick: handleDismiss,
            }}
          />
        ) : (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: "Select another professional",
              onClick: handleSelectAnother,
            }}
            secondaryAction={
              isBlockedError
                ? undefined
                : {
                    label: "Close",
                    onClick: handleDismiss,
                  }
            }
          />
        )
      }
      onClose={isBlockedError ? () => {} : handleDismiss}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        <InfoBanner message={errorMessage} title={bannerTitle} variant="error" />

        {hasMissingDocuments && (
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(1),
            })}
          >
            <Text semibold sx={(theme) => ({ color: theme.palette.text.primary })} variant="body2">
              Missing documents:
            </Text>
            <Box
              component="ul"
              sx={(theme: Theme) => ({
                margin: 0,
                paddingLeft: theme.spacing(4),
              })}
            >
              {missingDocuments.map((document) => (
                <Box
                  key={document}
                  component="li"
                  sx={(theme: Theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                >
                  <Text variant="body2">{document}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {errorReasons.length > 1 && (
          <Box
            component="ul"
            sx={(theme: Theme) => ({
              margin: 0,
              paddingLeft: theme.spacing(4),
            })}
          >
            {errorReasons.map((reason) => (
              <Box
                key={reason}
                component="li"
                sx={(theme: Theme) => ({
                  color: theme.palette.text?.secondary,
                })}
              >
                <Text variant="body2">{ERROR_MESSAGES[reason]}</Text>
              </Box>
            ))}
          </Box>
        )}

        <Text
          sx={(theme: Theme) => ({
            color: theme.palette.text?.secondary,
          })}
          variant="body2"
        >
          {footerHelperText}
        </Text>
      </Box>
    </ResponsiveDialog>
  );
}
